// Shared, verified product facts for the marketing site.
// Single source of truth — used by homepage sections AND the dedicated
// marketing pages, so content is never duplicated by hand.

import { Search, Filter, Mail, Shield, FileArchive, Eye } from 'lucide-react'
import {
  SITE_URL,
  APP_REGISTER_URL,
  APP_LOGIN_URL,
  CONTACT_EMAIL,
} from './constants'

export { SITE_URL, APP_REGISTER_URL, APP_LOGIN_URL, CONTACT_EMAIL }

export const NE_STATES = [
  { abbr: 'MA', name: 'Massachusetts' },
  { abbr: 'CT', name: 'Connecticut' },
  { abbr: 'ME', name: 'Maine' },
  { abbr: 'NH', name: 'New Hampshire' },
  { abbr: 'RI', name: 'Rhode Island' },
  { abbr: 'VT', name: 'Vermont' },
] as const

export const FEATURES = [
  {
    icon: Search,
    title: 'Monitored Procurement Sources',
    desc: 'Municipal portals, state procurement systems, housing authorities, and transit agencies across MA, ME, NH, VT, RI, and CT. Checked daily.',
  },
  {
    icon: Filter,
    title: 'Trade-Matched Alerts',
    desc: 'Set your trade (electrical, plumbing, HVAC, painting, general construction, and 20+ more) and your counties. Only see bids relevant to your work.',
  },
  {
    icon: Mail,
    title: 'Daily Email Digest',
    desc: 'Every morning, get an email with new bids matching your trade and service area, sorted by deadline.',
  },
  {
    icon: Shield,
    title: 'Document Vault',
    desc: 'Store your W-9, insurance certificates, licenses, bonding, and certifications. Get alerts when documents expire.',
  },
  {
    icon: FileArchive,
    title: 'Bid Package Generator',
    desc: 'One click to assemble your bid submission: your documents from the Vault, your estimate, and submission instructions. Download as a zip or email directly.',
  },
  {
    icon: Eye,
    title: 'Watchlist Pipeline',
    desc: 'Track every bid from Watching → Preparing → Submitted → Won or Lost. Never lose track of where you stand.',
  },
] as const

export const STEPS = [
  {
    num: '01',
    icon: Filter,
    title: 'Set your trade and counties',
    desc: 'Pick your trade (electrical, plumbing, HVAC, etc.) and select which counties you work in across the 6 New England states.',
  },
  {
    num: '02',
    icon: Search,
    title: 'We monitor procurement sources daily',
    desc: 'ProcurAlerts checks municipal portals, state agencies, housing authorities, and transit agencies every day for new bids.',
  },
  {
    num: '03',
    icon: Mail,
    title: 'Get matched bids in your inbox',
    desc: 'Every morning you get a digest with bids that match your trade and service area, sorted by deadline. Add bids to your Watchlist and generate submission packages.',
  },
] as const

export interface Plan {
  name: string
  audience: string
  monthly: number
  annualMonthly: number
  badge?: string
  features: string[]
}

export const PLANS: Plan[] = [
  {
    name: 'Pro',
    audience: 'For individual contractors',
    monthly: 99,
    annualMonthly: 79,
    badge: 'Most Popular',
    features: [
      'All 6 New England states',
      'Unlimited counties',
      'Unlimited bid alerts',
      'Daily email digest matched to your trade and service area',
      'Document Vault with expiry tracking',
      'Watchlist to track bids through Watching → Preparing → Submitted → Won/Lost',
      'Generate Bid Package with auto-attached documents',
      '14-day free trial, cancel anytime',
    ],
  },
  {
    name: 'Team',
    audience: 'For companies with multiple estimators',
    monthly: 199,
    annualMonthly: 159,
    features: [
      'Everything in Pro, plus:',
      'Shared company profile across all team members',
      'Invite team members via email',
      'Each member gets their own trade and service area settings',
      '14-day free trial, cancel anytime',
    ],
  },
]

export const TRIAL_NOTE = '14-day free trial on all plans. Cancel anytime.'
export const TEAM_SIZE_NOTE =
  'Team plans permit up to 5 users under a single organization.'
